/**
 * Proposal Builder - Multi-step wizard for proposal generation
 * Uses the report job infrastructure
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Select, ReportJobMonitor, Tabs } from '@tracekit/core';
import type { Tab } from '@tracekit/core';
import { FileText, Plus, Trash2, AlertCircle, Loader2, Search, Brain, Zap, StopCircle, CheckCircle, ShieldCheck, Target, ListChecks, FileCheck, FileCode, X, ExternalLink, Download, Star } from 'lucide-react';
import { useWorkspace, useProposalTemplate, useProposalTemplateStream, useProposalJobSubmit, useProposalJobStatus, CustomSectionsTab } from '@tracekit/core';
import { TemplateMethodSelector, type TemplateMethod } from './TemplateMethodSelector';
import { TemplatePreview } from './TemplatePreview';
import { getOpportunityExtractionStatusV3, getOpportunityStructureV3, generateTemplateFromRFPStructure } from '../../api/opportunities';
import { RFPUploadModal } from './RFPUploadModal';
import { TemplateImportModal } from './TemplateImportModal';
import { proposalGenerationInProgressBody, reportGenerationRunningFooterNote } from '../../copy/proposalGenerationTiming';
import { ProductionTemplateModal } from './ProductionTemplateModal';
import { ComplianceMatrixView } from '../ComplianceMatrixView';
import { QAPassQuestions } from '../QAPassQuestions';
import { TemplateVariablesEditor } from '../TemplateVariablesEditor';
// Import structure-viewer components for review modal
import { NormalizedCriteriaIndex } from '../structure-viewer/NormalizedCriteriaIndex';
import { EvaluatorLedOutlineView } from '../structure-viewer/EvaluatorLedOutlineView';
import { MarkdownTemplateViewer } from '../structure-viewer/MarkdownTemplateViewer';
import { getAPIClient } from '@tracekit/core';
import { useRFPJobStream } from '../../hooks/useRFPJobStream';
import { useRFPV3Extraction } from '../../hooks/useRFPV3Extraction';
import { formatQuestionTextForDisplay } from '../../utils/formatQuestionText';

interface ProposalBuilderProps {
  opportunityId: string;
  opportunityTitle: string;
  clientName: string;
  resumePreviousProgress?: boolean;
  onComplete?: (jobId: string) => void;
  onCancel?: () => void;
}

export function ProposalBuilder({
  opportunityId,
  opportunityTitle,
  clientName,
  resumePreviousProgress = true,
  onComplete,
  onCancel,
}: ProposalBuilderProps) {
  const { projectId } = useWorkspace();
  const api = getAPIClient();
  const [step, setStep] = useState(0); // Step 0: Method selection
  const [selectedMethod, setSelectedMethod] = useState<TemplateMethod | null>(null);
  const [showRFPUpload, setShowRFPUpload] = useState(false);
  const [showTemplateImport, setShowTemplateImport] = useState(false);
  const [showProductionTemplateModal, setShowProductionTemplateModal] = useState(false);
  const [rfpArtifactIds, setRfpArtifactIds] = useState<string[]>([]);
  const [reportId, setReportId] = useState<string | null>(null); // For custom sections after generation
  const [questions, setQuestions] = useState<Array<{ section: string; question: string }>>([]);
  const [proposalName, setProposalName] = useState('');
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null);
  const [useStreaming, setUseStreaming] = useState(true); // Use streaming by default
  const [templateSource, setTemplateSource] = useState<string>(''); // Track template source
  const [importedTemplate, setImportedTemplate] = useState<{
    sections: Array<{ name: string; description: string }>;
    default_questions: Array<{ section: string; question: string }>;
    template_source?: string;
    entity_types_by_question?: Record<string, string[]>;
  } | null>(null); // Store imported template
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [isInitializingResume, setIsInitializingResume] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [extractedStructure, setExtractedStructure] = useState<any>(null);
  const [reviewTab, setReviewTab] = useState<'outline' | 'criteria' | 'markdown'>('outline');
  /** Resume flow found a completed proposal job — offer open vs start fresh instead of a vague "check reports". */
  const [existingCompletedProposal, setExistingCompletedProposal] = useState<{
    jobId: string;
    reportId: string;
  } | null>(null);
  const [uploadedArtifacts, setUploadedArtifacts] = useState<Array<{
    id: string;
    filename: string;
    chunks_count: number;
    entities_count: number;
  }>>([]); // Store uploaded artifact info
  // Full production template result for report metadata (staff_requirements, company_requirements) and Staff Assignments tab
  const productionTemplateResultRef = React.useRef<any>(null);
  const didRunResumeInitRef = React.useRef(false);
  /** Parent callbacks are often inline — keep latest without putting them in effect deps (avoids update loops). */
  const onCompleteRef = React.useRef<ProposalBuilderProps['onComplete']>(onComplete);
  /** Fire `onComplete` at most once per submitted job + report id (polling refetches would retrigger otherwise). */
  const proposalCompletionHandledRef = React.useRef<string | null>(null);

  // Legacy streaming template extraction (DEPRECATED - kept for backward compatibility)
  // TODO: Remove this after all users migrate to production template
  const templateStream = useProposalTemplateStream();
  
  // RFP job streaming for monitoring document processing
  const rfpJobStream = useRFPJobStream();
  
  // V3 RFP Structure Extraction
  const [v3ExtractionState, v3ExtractionActions] = useRFPV3Extraction({
    opportunityId,
    projectId: projectId || '',
    autoFetch: true, // Auto-check if extraction already exists
    pollInterval: 2000,
  });

  // Re-sync extraction-status when upload modal opens (API uses `status`, not only `extraction_status`)
  useEffect(() => {
    if (showRFPUpload && opportunityId && projectId) {
      void v3ExtractionActions.checkStatus();
    }
  }, [showRFPUpload, opportunityId, projectId, v3ExtractionActions.checkStatus]);

  // Ref to prevent multiple concurrent template generation calls
  const isGeneratingRef = React.useRef(false);
  // Track which artifacts we've already generated templates for to prevent duplicates
  const generatedArtifactIdsRef = React.useRef<Set<string>>(new Set());
  /** When true, next ProductionTemplateModal onClose must NOT clear rfpArtifactIds (accept or proposal submitted from modal). */
  const productionTemplateModalSuccessRef = React.useRef(false);
  
  // Fallback to non-streaming if streaming not available
  const { data: template, isLoading: templateLoading, error: templateError } = useProposalTemplate(
    opportunityId,
    projectId || null,
    !useStreaming // Only use non-streaming if streaming is disabled
  );

  // Start streaming when method is selected and it's AI-derived
  useEffect(() => {
    if (selectedMethod === 'ai_derived' && useStreaming && projectId && opportunityId && !templateStream.isStreaming && !templateStream.template) {
      templateStream.startStream(opportunityId, projectId, rfpArtifactIds.length > 0 ? rfpArtifactIds : undefined);
    }
  }, [selectedMethod, useStreaming, projectId, opportunityId, templateStream.isStreaming, templateStream.template, templateStream.startStream, rfpArtifactIds]);
  
  // Monitor template stream completion when generating from RFP
  useEffect(() => {
    let cancelled = false;

    // Phase 1 ends with awaiting_approval; keep the streaming wizard and continue automatically
    // (same as legacy "Approve & Continue" without the full-page Step 0.5 funnel).
    if (
      isGeneratingTemplate &&
      templateStream.stage === 'awaiting_approval' &&
      step === 0 &&
      projectId
    ) {
      console.log(
        '[ProposalBuilder] awaiting_approval (Phase 1 gate): auto-approving and starting Phase 2 stream'
      );

      (async () => {
        try {
          const statusData = await getOpportunityExtractionStatusV3(
            opportunityId,
            projectId
          );
          if (cancelled) return;

          console.log('[ProposalBuilder] Structure gate status:', {
            extraction_id: statusData.extraction_id,
            sections: statusData.sections_count,
            requirements: statusData.requirements_count,
            has_structure: !!statusData.structure,
          });

          if (!statusData.structure) {
            console.error(
              '[ProposalBuilder] awaiting_approval but no structure in status payload'
            );
            setIsGeneratingTemplate(false);
            isGeneratingRef.current = false;
            alert(
              'Structure extraction completed but failed to load structure data. Please refresh.'
            );
            return;
          }

          try {
            const client = getAPIClient();
            await client.post(
              `/api/rfp/${opportunityId}/rfp/structure/approve`,
              {
                extraction_id: statusData.extraction_id,
                approved: true,
              },
              {
                params: { project_id: projectId },
                timeout: 30000,
              }
            );
            console.log('[ProposalBuilder] Structure auto-approved for Phase 2');
          } catch (approveErr) {
            console.warn(
              '[ProposalBuilder] Approve request failed; continuing to Phase 2 stream anyway:',
              approveErr
            );
          }

          if (cancelled) return;

          const artifacts =
            rfpArtifactIds.length > 0 ? rfpArtifactIds : undefined;
          templateStream.startStream(
            opportunityId,
            projectId,
            artifacts,
            true
          );
        } catch (error) {
          if (cancelled) return;
          console.error(
            '[ProposalBuilder] Failed to advance past structure gate:',
            error
          );
          setIsGeneratingTemplate(false);
          isGeneratingRef.current = false;
          alert(
            'Structure extraction completed but Phase 2 could not start. Please try again.'
          );
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (isGeneratingTemplate && templateStream.stage === 'awaiting_approval' && step !== 0) {
      console.warn(
        '[ProposalBuilder] Unexpected awaiting_approval with step !== 0; keeping progress UI',
        step
      );
    }
    
    // PHASE 2 COMPLETION: Only move to Step 1 when we have actual questions generated
    // Don't treat structure_extraction_complete as "done" - we need the full template with questions
    const hasActualQuestions = templateStream.template?.default_questions && templateStream.template.default_questions.length > 0;
    const isPhase2Complete = templateStream.stage === 'complete' && hasActualQuestions;
    
    // Debug logging for stream state
    if (isGeneratingTemplate && templateStream.stage) {
      console.log('[ProposalBuilder] Stream state check:', {
        stage: templateStream.stage,
        hasTemplate: !!templateStream.template,
        questionsCount: templateStream.template?.default_questions?.length || 0,
        sectionsCount: templateStream.template?.sections?.length || 0,
        hasActualQuestions,
        isPhase2Complete,
        willMoveToStep1: isPhase2Complete && !!templateStream.template
      });
    }
    
    if (isGeneratingTemplate && isPhase2Complete && templateStream.template) {
      console.log('[ProposalBuilder] ✅ PHASE 2 COMPLETE - Template generation stream completed:', {
        stage: templateStream.stage,
        sections: templateStream.template.sections?.length || 0,
        questions: templateStream.template.default_questions?.length || 0,
        source: templateStream.template.template_source,
        sampleSections: templateStream.template.sections?.slice(0, 3).map((s: any) => s.name || s.title),
        sampleQuestions: templateStream.template.default_questions?.slice(0, 3).map((q: any) => ({
          section: q.section,
          questionPreview: q.question?.substring(0, 80) + '...'
        }))
      });
      
      // Store the template
      setImportedTemplate(templateStream.template);
      setQuestions(templateStream.template.default_questions || []);
      setTemplateSource(templateStream.template.template_source || 'rfp_documents');
      
      // Log question breakdown by section
      if (templateStream.template.default_questions?.length > 0) {
        const questionBreakdown: Record<string, number> = {};
        templateStream.template.default_questions.forEach((q: any) => {
          questionBreakdown[q.section || 'Unknown'] = (questionBreakdown[q.section || 'Unknown'] || 0) + 1;
        });
        console.log('[ProposalBuilder] ✅ Question breakdown by section:', questionBreakdown);
      } else {
        console.error('[ProposalBuilder] ❌ NO QUESTIONS in template! This should not happen for Phase 2.');
      }
      
      // Close modal and advance to step 1
      console.log('[ProposalBuilder] Moving to Step 1 with generated template');
      setShowRFPUpload(false);
      setStep(1);
      setIsGeneratingTemplate(false);
      isGeneratingRef.current = false;
    } else if (isGeneratingTemplate && templateStream.stage === 'error') {
      console.error('[ProposalBuilder] Template generation stream error:', templateStream.error);
      alert(`Failed to generate template: ${templateStream.error || 'Unknown error'}`);
      setIsGeneratingTemplate(false);
      isGeneratingRef.current = false;
      // Remove from generated set on error so user can retry
      if (rfpArtifactIds.length > 0) {
        const artifactKey = rfpArtifactIds.sort().join(',');
        generatedArtifactIdsRef.current.delete(artifactKey);
      }
    }
  }, [
    isGeneratingTemplate,
    templateStream.stage,
    templateStream.template,
    templateStream.error,
    templateStream.startStream,
    rfpArtifactIds,
    opportunityId,
    projectId,
    step,
  ]);

  // Use streaming template if available, otherwise fall back to non-streaming
  // If streaming completed with empty template, use non-streaming as fallback
  // Policy pack fallback templates are valid even if RAG failed
  // Prioritize imported template over streaming/non-streaming templates
  const finalTemplate = importedTemplate || 
    (templateStream.template && 
      (templateStream.template.sections.length > 0 || templateStream.template.default_questions.length > 0)
      ? templateStream.template 
      : template);
  const isLoading = templateStream.isStreaming || (templateLoading && !templateStream.template && !importedTemplate);
  const hasError = templateStream.error || templateError;
  
  // Check if we have a valid template (including policy pack fallback and imported templates)
  const hasValidTemplate = (importedTemplate && importedTemplate.sections && importedTemplate.sections.length > 0 && importedTemplate.default_questions && importedTemplate.default_questions.length > 0) ||
    (finalTemplate && 
      finalTemplate.sections && finalTemplate.sections.length > 0 && 
      finalTemplate.default_questions && finalTemplate.default_questions.length > 0);

  const submitMutation = useProposalJobSubmit();
  const { data: jobStatus } = useProposalJobStatus(submittedJobId, !!submittedJobId);

  React.useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  React.useEffect(() => {
    proposalCompletionHandledRef.current = null;
  }, [submittedJobId]);

  // Initialize questions from template (only if not already set from import)
  useEffect(() => {
    if (finalTemplate && questions.length === 0 && !importedTemplate) {
      const templateQuestions = finalTemplate.default_questions || [];
      console.log('[ProposalBuilder] Initializing questions from template:', {
        templateQuestionsCount: templateQuestions.length,
        sectionsCount: finalTemplate.sections?.length || 0,
        ratio: templateQuestions.length / (finalTemplate.sections?.length || 1)
      });
      setQuestions(templateQuestions);
    }
  }, [finalTemplate, questions.length, importedTemplate]);
  
  // Log template state changes for debugging
  useEffect(() => {
    if (finalTemplate) {
      console.log('[ProposalBuilder] Template state updated:', {
        hasSections: !!finalTemplate.sections,
        sectionsCount: finalTemplate.sections?.length || 0,
        hasQuestions: !!finalTemplate.default_questions,
        questionsCount: finalTemplate.default_questions?.length || 0,
        templateSource: finalTemplate.template_source,
        importedTemplate: !!importedTemplate,
        questionsStateCount: questions.length
      });
    }
  }, [finalTemplate, importedTemplate, questions.length]);

  // Handle job completion (guard: unstable parent `onComplete` + repeated completed refetches)
  useEffect(() => {
    if (!submittedJobId || jobStatus?.status !== 'completed') return;
    const reportIdFromJob = jobStatus.output_data?.report_id;
    if (!reportIdFromJob) return;

    const completionKey = `${submittedJobId}:${reportIdFromJob}`;
    if (proposalCompletionHandledRef.current === completionKey) return;
    proposalCompletionHandledRef.current = completionKey;

    setReportId(reportIdFromJob);
    onCompleteRef.current?.(reportIdFromJob);
  }, [
    submittedJobId,
    jobStatus?.status,
    jobStatus?.output_data?.report_id,
  ]);

  // Resume flow: when user re-opens Proposal, jump to their highest reached stage.
  useEffect(() => {
    if (!resumePreviousProgress) {
      didRunResumeInitRef.current = true;
      setIsInitializingResume(false);
      return;
    }

    if (!projectId || didRunResumeInitRef.current) return;
    didRunResumeInitRef.current = true;

    const runResumeInit = async () => {
      try {
        // Step 5: existing/final proposal job for this opportunity -> open final progress modal directly
        const jobs = await api.get<any[]>('/jobs', { params: { project_id: projectId } });
        const latestProposalJob = (jobs || [])
          .filter((j) => {
            if (j?.type !== 'report_generation') return false;
            const reportType = j?.input_data?.report_type;
            const oppId = j?.input_data?.metadata?.opportunity_id;
            return reportType === 'rfp_proposal' && String(oppId) === String(opportunityId);
          })
          .sort(
            (a, b) =>
              new Date(b?.created_at || 0).getTime() -
              new Date(a?.created_at || 0).getTime()
          )[0];

        if (
          latestProposalJob &&
          ['pending', 'queued', 'running', 'checkpointed', 'completed'].includes(
            String(latestProposalJob.status || '')
          )
        ) {
          const st = String(latestProposalJob.status || '');
          if (st === 'completed') {
            let reportId: string | undefined =
              latestProposalJob.output_data?.report_id != null
                ? String(latestProposalJob.output_data.report_id)
                : undefined;
            if (!reportId) {
              try {
                const detail = await api.get<{ output_data?: { report_id?: string } }>(
                  `/reports/jobs/${latestProposalJob.id}/status`
                );
                reportId =
                  detail?.output_data?.report_id != null
                    ? String(detail.output_data.report_id)
                    : undefined;
              } catch {
                reportId = undefined;
              }
            }
            if (reportId) {
              setExistingCompletedProposal({
                jobId: String(latestProposalJob.id),
                reportId,
              });
              return;
            }
          }
          if (['pending', 'queued', 'running', 'checkpointed'].includes(st) || st === 'completed') {
            setSubmittedJobId(String(latestProposalJob.id));
            setStep(3);
            return;
          }
        }

        // Step 4/3: production template flow (completed or in-progress)
        let latestProductionJobId: string | null = null;
        try {
          const history = await api.get<{ jobs?: Array<{ id: string }> }>(
            `/api/opportunities/${opportunityId}/production-template/history`,
            { params: { project_id: projectId, limit: 1 } }
          );
          latestProductionJobId = history?.jobs?.[0]?.id ?? null;
        } catch {
          latestProductionJobId = null;
        }

        if (latestProductionJobId) {
          const productionStatus = await api.get<any>(
            `/api/opportunities/${opportunityId}/production-template/status/${latestProductionJobId}`,
            { params: { project_id: projectId } }
          );

          if (productionStatus?.status === 'completed' && productionStatus?.result) {
            // Resume directly at template-editing step (step 4 equivalent)
            const result = productionStatus.result;
            const convertedTemplate = {
              sections: result.sections || [],
              default_questions: result.questions || [],
              template_source: 'production_template',
              metadata: result.metadata,
            };
            setImportedTemplate(convertedTemplate);
            setQuestions(result.questions || []);
            setTemplateSource('production_template');
            productionTemplateResultRef.current = result;
            setSelectedMethod('production_template');
            setStep(1);
            return;
          }

          if (
            ['pending', 'queued', 'running'].includes(
              String(productionStatus?.status || '')
            )
          ) {
            // Resume in-progress production template modal (step 3/4 equivalent)
            const idsFromStatus =
              productionStatus?.metadata?.artifact_ids ||
              productionStatus?.result?.metadata?.artifact_ids ||
              [];
            let artifactIds: string[] = Array.isArray(idsFromStatus)
              ? idsFromStatus.map(String).filter(Boolean)
              : [];

            if (artifactIds.length === 0) {
              try {
                const docs = await api.get<{ documents?: Array<{ artifact_id: string }> }>(
                  `/api/opportunities/${opportunityId}/rfp-documents`,
                  { params: { project_id: projectId } }
                );
                artifactIds = (docs?.documents || [])
                  .map((d) => String(d.artifact_id))
                  .filter(Boolean);
              } catch {
                artifactIds = [];
              }
            }

            if (artifactIds.length > 0) {
              setRfpArtifactIds(artifactIds);
            }
            setSelectedMethod('production_template');
            setShowProductionTemplateModal(true);
            setStep(0);
            return;
          }
        }

        // Step 2 fallback: docs exist, reopen upload/documents step instead of method selection.
        try {
          const docs = await api.get<{ documents?: Array<{ artifact_id: string }> }>(
            `/api/opportunities/${opportunityId}/rfp-documents`,
            { params: { project_id: projectId } }
          );
          const artifactIds = (docs?.documents || [])
            .map((d) => String(d.artifact_id))
            .filter(Boolean);
          if (artifactIds.length > 0) {
            setRfpArtifactIds(artifactIds);
            setSelectedMethod('upload_rfp');
            setShowRFPUpload(true);
            setStep(0);
          }
        } catch {
          // Keep default step 0 if docs endpoint is unavailable
        }
      } catch (error) {
        console.warn('[ProposalBuilder] Resume init failed, falling back to step 0:', error);
      } finally {
        setIsInitializingResume(false);
      }
    };

    void runResumeInit();
  }, [api, opportunityId, projectId, resumePreviousProgress]);

  const handleAddQuestion = () => {
    setQuestions([...questions, { section: '', question: '' }]);
  };

  const handleRemoveQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index: number, field: 'section' | 'question', value: string) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  // Handle method selection
  const handleMethodSelect = (method: TemplateMethod) => {
    setSelectedMethod(method);
    
    if (method === 'upload_rfp') {
      // Reset generation state when opening RFP upload modal
      setIsGeneratingTemplate(false);
      isGeneratingRef.current = false;
      generatedArtifactIdsRef.current.clear();
      // Reset template stream if needed
      if (templateStream.stage === 'complete' || templateStream.stage === 'error') {
        templateStream.reset();
      }
      setShowRFPUpload(true);
    } else if (method === 'import_report') {
      setShowTemplateImport(true);
    } else if (method === 'ai_derived') {
      // Require RFP selection/upload when no artifacts — avoids reusing stale IDs from a cancelled production job
      if (rfpArtifactIds.length === 0) {
        setShowRFPUpload(true);
        setStep(0);
      } else {
        setStep(1);
      }
    } else if (method === 'production_template') {
      // Production template: Open modal for RFP upload if needed, then start generation
      if (rfpArtifactIds.length === 0) {
        // No RFP uploaded yet - open RFP upload modal first
        setShowRFPUpload(true);
      } else {
        // RFP already uploaded - open production template modal
        setShowProductionTemplateModal(true);
      }
    }
  };

  // Handle cached template load from RFPUploadModal
  const handleCachedTemplateLoad = (template: any) => {
    console.log('[ProposalBuilder] Loading cached template:', {
      sections: template.sections?.length || 0,
      questions: template.default_questions?.length || 0,
      source: template.template_source
    });
    
    setImportedTemplate(template);
    setQuestions(template.default_questions || []);
    setTemplateSource(template.template_source || 'cached_template');
    setShowRFPUpload(false);
    setStep(1);
  };
  
  // Handle RFP upload completion - called ONLY when user clicks "Generate Template" button
  // This is the ONLY place template generation should be triggered - never automatic
  const handleRFPUploadComplete = async (artifactIds: string[], jobId: string) => {
    console.log('[ProposalBuilder] handleRFPUploadComplete called:', {
      artifactIds,
      artifactIdsCount: artifactIds?.length || 0,
      jobId,
      selectedMethod,
      isGeneratingRef: isGeneratingRef.current,
      isGeneratingTemplate,
      templateStreamStage: templateStream.stage
    });
    
    // Guard: Ensure we have artifact IDs
    if (!artifactIds || artifactIds.length === 0) {
      console.warn('[ProposalBuilder] No artifact IDs provided, cannot generate template');
      return;
    }
    
    // Store artifact IDs
    setRfpArtifactIds(artifactIds);
    
    // Fetch artifact info from RFP documents endpoint (more efficient than individual queries)
    if (artifactIds.length > 0 && projectId) {
      try {
        const api = getAPIClient();
        // Use RFP documents endpoint which returns all info in one call
        const rfpDocsResponse = await api.get(`/api/opportunities/${opportunityId}/rfp-documents`, {
          params: { project_id: projectId },
        });
        const allDocs = rfpDocsResponse.data?.documents || [];
        // Filter to only the uploaded artifacts
        const uploadedDocs = allDocs.filter((doc: any) => artifactIds.includes(doc.artifact_id));
        setUploadedArtifacts(uploadedDocs.map((doc: any) => ({
          id: doc.artifact_id,
          filename: doc.filename,
          chunks_count: doc.chunks_count || 0,
          entities_count: doc.entities_count || 0,
        })));
      } catch (error) {
        console.error('[ProposalBuilder] Failed to fetch artifact info:', error);
        // Continue anyway - we have artifact IDs
      }
    }
    
    // 🔥 CRITICAL: Route to correct flow based on selected method.
    // When selectedMethod is 'upload_rfp' (the default when a user clicks "Generate Template"
    // on an opportunity with existing files), route to the production_template flow — it is the
    // current standard and produces richer, criteria-driven templates.
    if (selectedMethod === 'production_template' || selectedMethod === 'upload_rfp') {
      console.log('[ProposalBuilder] 🚀 PRODUCTION TEMPLATE FLOW - Opening ProductionTemplateModal');
      setSelectedMethod('production_template');
      // Close RFP upload modal
      setShowRFPUpload(false);
      // Open production template modal
      setShowProductionTemplateModal(true);
      return;
    }
    
    // 🔥 LEGACY AI-DERIVED FLOW: V3 Structure extraction (only used when explicitly requested)
    console.log('[ProposalBuilder] 🎯 LEGACY AI-DERIVED FLOW - Starting V3 structure extraction');
    
    // Guard: Prevent multiple concurrent template generation calls
    if (isGeneratingRef.current || isGeneratingTemplate || templateStream.isStreaming) {
      console.warn('[ProposalBuilder] Template generation already in progress, ignoring duplicate call', {
        isGeneratingRef: isGeneratingRef.current,
        isGeneratingTemplate,
        isStreaming: templateStream.isStreaming,
        stage: templateStream.stage
      });
      return;
    }
    
    // Reset template stream if it's in a completed/error state to allow regeneration
    if (templateStream.stage === 'complete' || templateStream.stage === 'error') {
      console.log('[ProposalBuilder] Resetting template stream state before new generation');
      templateStream.reset();
    }
    
    console.log('[ProposalBuilder] 🎯 PHASE 1 START - Fresh upload, starting structure extraction only');
    console.log('[ProposalBuilder] Artifact IDs:', artifactIds);
    
    // IMPORTANT: Clear any approved structure from previous runs
    // This forces the worker to do Phase 1 extraction and stop at awaiting_approval
    try {
      const client = getAPIClient();
      console.log('[ProposalBuilder] Clearing previous approved structure (if any)...');
      await client.delete(
        `/api/opportunities/${opportunityId}/rfp/structure/approved`,
        {
          params: { project_id: projectId }
        }
      );
      console.log('[ProposalBuilder] ✅ Previous approved structure cleared');
    } catch (error) {
      console.warn('[ProposalBuilder] No previous approved structure to clear (this is fine):', error);
      // Continue anyway - maybe there was no previous structure
    }
    
    // Start PHASE 1: Structure extraction with streaming
    setIsGeneratingTemplate(true);
    isGeneratingRef.current = true;
    
    console.log('[ProposalBuilder] Starting PHASE 1 structure extraction...', {
      opportunityId,
      projectId,
      artifactIds,
      artifactIdsCount: artifactIds.length
    });
    
    // Use streaming endpoint for real-time progress updates
    // Pass artifact IDs directly to the streaming endpoint
    try {
      // Start the stream - Worker will:
      // 1. Extract structure from PDF
      // 2. Store in Redis
      // 3. Send 'awaiting_approval' stage
      // 4. Frontend auto-approves and restarts stream for Phase 2 (same wizard UI)
      templateStream.startStream(opportunityId, projectId || '', artifactIds);
      
      // Monitor stream completion using useEffect
      // This will be handled by the useEffect below
    } catch (error: any) {
      console.error('[ProposalBuilder] Failed to start template generation stream, using non-streaming fallback:', error);
      // Fallback to non-streaming POST endpoint
      try {
        const api = getAPIClient();
        const templateData = await api.post(
          `/api/opportunities/${opportunityId}/proposal-template/from-rfp?project_id=${projectId}`,
          {
            artifact_ids: artifactIds,
            adapt_questions: true,
          }
        );
        
        const finalTemplate = templateData.data || templateData;
        setImportedTemplate(finalTemplate);
        setQuestions(finalTemplate.default_questions || []);
        setTemplateSource(finalTemplate.template_source || 'rfp_documents');
        
        setShowRFPUpload(false);
        setStep(1);
      } catch (fallbackError: any) {
        console.error('[ProposalBuilder] Non-streaming fallback also failed:', fallbackError);
        const errorMessage = fallbackError.response?.data?.detail || fallbackError.message || 'Unknown error';
        alert(`Failed to generate template: ${errorMessage}`);
        // Remove from generated set on error so user can retry
        const artifactKey = artifactIds.sort().join(',');
        generatedArtifactIdsRef.current.delete(artifactKey);
      } finally {
        setIsGeneratingTemplate(false);
        isGeneratingRef.current = false;
      }
    }
  };
  
  // Monitor RFP job completion - just notify parent, don't auto-generate template
  // Template generation will be triggered by user clicking "Generate Template" button
  useEffect(() => {
    // When job completes, the modal will show confirmation step
    // User must click "Generate Template" button to proceed
    // This prevents automatic template generation and allows user to review files first
    if (rfpJobStream.stage === 'complete' && uploadedArtifacts.length === 0 && rfpArtifactIds.length > 0) {
      // Job completed but we haven't fetched artifact info yet
      // The modal will handle showing the confirmation step
      console.log('[ProposalBuilder] RFP job completed, waiting for user confirmation to generate template');
    }
  }, [rfpJobStream.stage, rfpArtifactIds, uploadedArtifacts.length]);

  // Handle template import
  const handleTemplateImport = (template: {
    sections: Array<{ name: string; description: string }>;
    default_questions: Array<{ section: string; question: string }>;
  }) => {
    // Store the imported template
    setImportedTemplate(template);
    setQuestions(template.default_questions || []);
    setTemplateSource('previous_report_import');
    setShowTemplateImport(false);
    setStep(1);
  };

  // Handle production template completion
  const handleProductionTemplateComplete = (result: any) => {
    console.log('[ProposalBuilder] Production template complete:', {
      sections: result.sections?.length || 0,
      questions: result.questions?.length || 0,
      metadata: result.metadata
    });

    // Convert production template result to ProposalBuilder format
    const convertedTemplate = {
      sections: result.sections || [],
      default_questions: result.questions || [],
      template_source: 'production_template',
      metadata: result.metadata,
    };

    // DON'T update state yet - wait for user to click "Accept & Use Template"
    // This prevents auto-navigation to ProposalBuilder
    // Store the result temporarily but don't set it until user accepts
    console.log('[ProposalBuilder] Production template result received, waiting for user acceptance...');
    
    // Only update state and navigate when user explicitly accepts
    // This will be called from handleAcceptTemplate in ProductionTemplateModal
  };
  
  // Separate handler for when user actually accepts the template
  const handleProductionTemplateAccepted = (result: any) => {
    console.log('[ProposalBuilder] User accepted production template, proceeding...');
    
    // Convert production template result to ProposalBuilder format
    const convertedTemplate = {
      sections: result.sections || [],
      default_questions: result.questions || [],
      template_source: 'production_template',
      metadata: result.metadata,
    };

    setImportedTemplate(convertedTemplate);
    setQuestions(result.questions || []);
    setTemplateSource('production_template');
    productionTemplateResultRef.current = result; // For report metadata (template_result) and Staff Assignments tab
    setIsGeneratingTemplate(false);
    setShowProductionTemplateModal(false);
    setStep(1);
  };

  // Handle production template error
  const handleProductionTemplateError = (error: string) => {
    console.error('[ProposalBuilder] Production template error:', error);
    // Error is already displayed in the modal, just log it here
  };

  const handleProductionTemplateModalClose = () => {
    setShowProductionTemplateModal(false);
    setIsGeneratingTemplate(false);
    const succeeded = productionTemplateModalSuccessRef.current;
    productionTemplateModalSuccessRef.current = false;
    if (succeeded) {
      return;
    }
    // Cancel / close without accepting: drop stale artifact IDs so the next flow does not skip RFP upload
    // or start AI-derived with the wrong documents.
    setRfpArtifactIds([]);
    setUploadedArtifacts([]);
  };

  const handleSubmit = async () => {
    if (!projectId) return;

    // When retrying after a failed job, disable cache so we don't reuse possibly bad cached answers
    const isRetryAfterFailure = jobStatus?.status === 'failed';

    try {
      const result = await submitMutation.mutateAsync({
        opportunityId,
        projectId,
        clientName: clientName || 'Government Agency',
        questions,
        metadata: {
          proposal_name: proposalName || opportunityTitle,
          include_past_proposals: true,
          include_evidence: true,
          // Include template result so report.metadata.template_result has staff_requirements + company_requirements for Staff Assignments tab
          ...(productionTemplateResultRef.current
            ? { template_result: productionTemplateResultRef.current }
            : {}),
        } as any, // TypeScript workaround - metadata accepts additional fields at runtime
        enable_cache: !isRetryAfterFailure,
      });

      setSubmittedJobId(result.id);
      setStep(3); // Move to monitoring step
    } catch (error) {
      console.error('Proposal generation failed:', error);
    }
  };

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">Select a project to create proposals</p>
      </Card>
    );
  }

  if (isInitializingResume) {
    return (
      <div className="flex w-full min-w-0 min-h-full flex-col items-center justify-center py-10 px-4">
        <Card className="w-full max-w-lg">
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <Loader2 className="h-10 w-10 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Checking your previous proposal progress...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Completed proposal already exists (resume) — direct link + explicit fresh start
  if (existingCompletedProposal) {
    const proposalsUrl = `/rfp/proposals-management?tab=list&reportId=${encodeURIComponent(
      existingCompletedProposal.reportId
    )}`;
    return (
      <div className="mx-auto max-w-lg">
        <Card>
          <div className="space-y-4 p-2">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-8 w-8 shrink-0 text-green-600 dark:text-green-400" />
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-theme-text">You already have a proposal</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  A completed proposal run is on file for this opportunity. Open it to view or edit, or start a{' '}
                  <strong>new</strong> proposal from scratch (template and generation setup again).
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2 sm:flex-row">
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => {
                  window.location.href = proposalsUrl;
                }}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open existing proposal
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setExistingCompletedProposal(null);
                  setStep(0);
                }}
              >
                Start a new proposal
              </Button>
            </div>
            {onCancel && (
              <Button variant="outline" className="w-full" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Step 0: Method Selection
  if (step === 0) {
    return (
      <div className="max-w-5xl mx-auto">
        <Card>
          <TemplateMethodSelector
            onSelectMethod={handleMethodSelect}
            onCancel={onCancel}
          />
        </Card>

        {/* RFP Upload Modal - Keep open during template generation */}
        {showRFPUpload && (
          <RFPUploadModal
            isOpen={showRFPUpload}
            onClose={() => {
              // Only allow closing if not generating template
              if (!isGeneratingTemplate && !templateStream.isStreaming) {
                // Reset all generation state when modal closes
                setIsGeneratingTemplate(false);
                isGeneratingRef.current = false;
                // Clear generated artifacts ref so user can regenerate if needed
                generatedArtifactIdsRef.current.clear();
                // Reset template stream to allow fresh generation
                templateStream.reset();
                setShowRFPUpload(false);
              }
            }}
            opportunityId={opportunityId}
            projectId={projectId!}
            onUploadComplete={handleRFPUploadComplete}
            onCachedTemplateLoad={handleCachedTemplateLoad}
            isGeneratingTemplate={isGeneratingTemplate || templateStream.isStreaming}
            templateStream={templateStream}
            disableCacheCheck={true}
            v3StructureExtraction={{
              status: v3ExtractionState.status,
              isExtracting: v3ExtractionState.isExtracting,
              isLoading: v3ExtractionState.isLoading,
              errorMessage: v3ExtractionState.errorMessage,
            }}
          />
        )}

        {/* Template Import Modal */}
        {showTemplateImport && (
          <TemplateImportModal
            isOpen={showTemplateImport}
            onClose={() => setShowTemplateImport(false)}
            opportunityId={opportunityId}
            projectId={projectId!}
            onTemplateSelected={handleTemplateImport}
          />
        )}

        {/* Production Template Modal */}
        {showProductionTemplateModal && rfpArtifactIds.length > 0 && (
          <ProductionTemplateModal
            isOpen={showProductionTemplateModal}
            onClose={handleProductionTemplateModalClose}
            opportunityId={opportunityId}
            opportunityTitle={opportunityTitle}
            projectId={projectId!}
            artifactIds={rfpArtifactIds}
            onComplete={handleProductionTemplateAccepted}
            onError={handleProductionTemplateError}
            onProposalJobSubmitted={(jobId: string) => {
              productionTemplateModalSuccessRef.current = true;
              console.log('[ProposalBuilder] Proposal job submitted directly from template:', jobId);
              setSubmittedJobId(jobId);
              setShowProductionTemplateModal(false);
              setIsGeneratingTemplate(false);
              setStep(3); // Navigate to monitoring step
            }}
          />
        )}
      </div>
    );
  }

  // Step 0.5: Structure Review (after extraction, before template generation)
  if (step === 0.5 && extractedStructure) {
    return (
      <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)] flex flex-col">
        <Card className="p-6 flex flex-col h-full">
          {/* Header with inline action buttons */}
          <div className="mb-4 flex-shrink-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-theme-text mb-1">
                  RFP Structure Extracted Successfully
                </h2>
                <p className="text-sm text-theme-text-secondary">
                  Review the extracted structure below. When ready, proceed to generate the proposal template.
                </p>
              </div>
              {/* Action buttons on the right */}
              <div className="flex gap-3 ml-6">
                <Button
                  onClick={() => {
                    setStep(0);
                    setExtractedStructure(null);
                    setShowRFPUpload(true);
                  }}
                  variant="outline"
                  size="sm"
                >
                  Back to Upload
                </Button>
                <Button
                  onClick={async () => {
                    console.log('[ProposalBuilder] 🎯 PHASE 2 START - User approved structure');
                    console.log('[ProposalBuilder] Artifact IDs:', rfpArtifactIds);
                    
                    // Validate we have artifact IDs
                    if (!rfpArtifactIds || rfpArtifactIds.length === 0) {
                      console.error('[ProposalBuilder] ❌ NO ARTIFACT IDs! Cannot start Phase 2.');
                      alert('Error: No RFP artifacts found. Please re-upload.');
                      return;
                    }
                    
                    // First, approve and save the structure to DB
                    try {
                      const client = getAPIClient();
                      console.log('[ProposalBuilder] Calling approval endpoint...');
                      await client.post(
                        `/api/rfp/${opportunityId}/rfp/structure/approve`,
                        { 
                          extraction_id: extractedStructure.extraction_id,
                          approved: true
                        },
                        { 
                          params: { project_id: projectId },
                          timeout: 30000
                        }
                      );
                      console.log('[ProposalBuilder] ✅ Structure approved and saved to DB');
                    } catch (error) {
                      console.error('[ProposalBuilder] ❌ Failed to approve structure:', error);
                      // Continue anyway - structure is already extracted
                    }
                    
                    // Clear the extracted structure from Step 0.5
                    setExtractedStructure(null);
                    
                    // ⚠️ LEGACY: This uses the OLD template generation with V3 structure
                    // For NEW production template, use "AI-Derived (Production)" card instead
                    console.log('[ProposalBuilder] 🚀 Starting LEGACY Phase 2 with', rfpArtifactIds.length, 'artifacts');
                    console.log('[ProposalBuilder] ⚠️ This is LEGACY flow - for better quality use "AI-Derived (Production)"');
                    
                    // Show streaming progress modal
                    setIsGeneratingTemplate(true);
                    
                    // Start the streaming job (legacy endpoint)
                    console.log('[ProposalBuilder] Calling templateStream.startStream()...');
                    templateStream.startStream(opportunityId, projectId!, rfpArtifactIds, true);
                    
                    console.log('[ProposalBuilder] ⏳ LEGACY Phase 2 job started');
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve & Continue
                </Button>
              </div>
            </div>
            
            {/* Quality metrics */}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                Quality: {extractedStructure.quality_score || 90}/100
              </span>
              <span className="text-theme-text-secondary">
                {extractedStructure.sections_count || 0} Sections
              </span>
              <span className="text-theme-text-secondary">
                {extractedStructure.requirements_count || 0} Requirements
              </span>
              <span className="text-theme-text-secondary">
                {extractedStructure.criteria_count || 0} Criteria
              </span>
            </div>
          </div>

          {/* PHASE 2 STREAMING PROGRESS - Shows when template generation is in progress */}
          {(() => {
            const shouldShow = isGeneratingTemplate && templateStream && templateStream.isStreaming;
            console.log('[ProposalBuilder] Progress UI check:', {
              isGeneratingTemplate,
              hasTemplateStream: !!templateStream,
              isStreaming: templateStream?.isStreaming,
              shouldShow,
              stage: templateStream?.stage,
              step
            });
            return shouldShow;
          })() && (
            <div className="mb-4 flex-shrink-0">
              <div className="p-4 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50  border-2 border-purple-300 rounded-lg shadow-lg">
                {/* Task Info Header */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-purple-200">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-purple-600" />
                    <span className="text-xs font-medium text-purple-700">
                      Phase 2: Template & Question Generation
                    </span>
                  </div>
                  {templateStream.elapsed !== undefined && (
                    <div className="text-xs text-purple-600 font-mono">
                      {templateStream.elapsed.toFixed(1)}s elapsed
                    </div>
                  )}
                </div>

                {/* Main Status */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="relative">
                    {templateStream.stage === 'started' ? (
                      <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
                    ) : templateStream.stage === 'generating' || templateStream.stage === 'question_generation' ? (
                      <Brain className="h-5 w-5 text-purple-600 animate-pulse" />
                    ) : templateStream.stage === 'retrieval' ? (
                      <Search className="h-5 w-5 text-purple-600 animate-pulse" />
                    ) : templateStream.stage === 'parsing' || templateStream.stage === 'structure_extraction' ? (
                      <FileText className="h-5 w-5 text-purple-600 animate-bounce" />
                    ) : templateStream.stage === 'complete' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Loader2 className="h-5 w-5 text-purple-600 animate-spin" />
                    )}
                    {templateStream.stage !== 'complete' && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-purple-900 mb-1">
                      {templateStream.message || 'Generating comprehensive template...'}
                    </div>
                    <div className="text-xs text-purple-700">
                      Stage: <span className="font-mono bg-purple-100 px-1 rounded">{templateStream.stage || 'processing'}</span>
                    </div>
                  </div>
                  {templateStream.progress !== undefined && (
                    <div className="text-right">
                      <div className="text-2xl font-bold text-purple-600">{templateStream.progress}%</div>
                      <div className="text-xs text-purple-500">progress</div>
                    </div>
                  )}
                </div>
                
                {/* Progress bar with percentage */}
                <div className="space-y-1">
                  <div className="h-3 bg-purple-200 rounded-full overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 via-indigo-600 to-purple-500 transition-all duration-500 relative"
                      style={{
                        width: `${templateStream.progress || 
                          (templateStream.stage === 'complete' ? 100 : 
                           templateStream.stage === 'generating' || templateStream.stage === 'question_generation' ? 60 : 
                           templateStream.stage === 'structure_extraction' ? 30 : 
                           templateStream.stage === 'parsing' ? 75 : 
                           templateStream.stage === 'retrieval' ? 50 : 10)}%`,
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                    </div>
                  </div>
                  
                  {/* Stage indicators */}
                  <div className="flex items-center justify-between text-[10px] text-purple-600 font-medium pt-1">
                    <span className={templateStream.stage === 'started' ? 'text-purple-900 font-bold' : ''}>
                      ▸ Start
                    </span>
                    <span className={templateStream.stage === 'structure_extraction' ? 'text-purple-900 font-bold' : ''}>
                      ▸ Extract
                    </span>
                    <span className={templateStream.stage === 'generating' || templateStream.stage === 'question_generation' ? 'text-purple-900 font-bold' : ''}>
                      ▸ Generate
                    </span>
                    <span className={templateStream.stage === 'parsing' ? 'text-purple-900 font-bold' : ''}>
                      ▸ Parse
                    </span>
                    <span className={templateStream.stage === 'complete' ? 'text-green-900 font-bold' : ''}>
                      ✓ Done
                    </span>
                  </div>
                </div>

                {/* Detailed steps */}
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress > 10 ? 'text-green-500' : 'text-gray-300'}`} />
                      <span className="text-purple-700">Section consolidation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress > 30 ? 'text-green-500' : 'text-gray-300'}`} />
                      <span className="text-purple-700">Content extraction</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress > 60 ? 'text-green-500' : 'text-gray-300'}`} />
                      <span className="text-purple-700">Per-section questions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress >= 100 ? 'text-green-500' : 'text-gray-300'}`} />
                      <span className="text-purple-700">Template complete</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabs - sticky header */}
          <div className="flex-shrink-0 border-b border-theme-border mb-4">
            <nav className="flex space-x-4">
              <button
                onClick={() => setReviewTab('outline')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  reviewTab === 'outline'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-theme-text-muted hover:text-theme-text'
                }`}
              >
                Structure Outline
              </button>
              <button
                onClick={() => setReviewTab('criteria')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  reviewTab === 'criteria'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-theme-text-muted hover:text-theme-text'
                }`}
              >
                Criteria & Requirements
              </button>
              <button
                onClick={() => setReviewTab('markdown')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  reviewTab === 'markdown'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-theme-text-muted hover:text-theme-text'
                }`}
              >
                Markdown Export
              </button>
            </nav>
          </div>

          {/* Tab Content - scrollable area */}
          <div className="flex-1 overflow-y-auto pr-2">
            {reviewTab === 'outline' && extractedStructure.structure && (
              <div className="space-y-2">
                <div className="flex justify-between items-center mb-3 sticky top-0 bg-theme-paper py-2 z-10">
                  <h3 className="text-base font-semibold">Sections ({extractedStructure.structure.sections?.length || 0})</h3>
                  <Button onClick={() => {
                    const json = JSON.stringify(extractedStructure.structure.sections, null, 2);
                    const blob = new Blob([json], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `sections-${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                  }} size="sm" variant="outline">
                    <Download className="h-4 w-4 mr-2" /> Export JSON
                  </Button>
                </div>
                {extractedStructure.structure.sections?.map((section: any, idx: number) => (
                  <Card key={idx} className="p-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm text-theme-text mb-1">
                          {section.name || section.heading}
                        </h4>
                        {section.content_preview && (
                          <p className="text-xs text-theme-text-secondary line-clamp-1">
                            {section.content_preview}
                          </p>
                        )}
                        <div className="flex gap-3 mt-1 text-xs text-gray-500">
                          {section.level && <span>Level {section.level}</span>}
                          {section.position && <span>Pos: {section.position}</span>}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {reviewTab === 'criteria' && extractedStructure.structure && (
              <div className="space-y-4">
                {/* Evaluation Criteria */}
                <div>
                  <div className="flex justify-between items-center mb-3 sticky top-0 bg-theme-paper py-2 z-10">
                    <h3 className="text-base font-semibold">Evaluation Criteria ({extractedStructure.structure.evaluation_criteria?.length || 0})</h3>
                    <Button onClick={() => {
                      const json = JSON.stringify(extractedStructure.structure.evaluation_criteria, null, 2);
                      const blob = new Blob([json], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `criteria-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                    }} size="sm" variant="outline">
                      <Download className="h-4 w-4 mr-2" /> Export
                    </Button>
                  </div>
                  {extractedStructure.structure.evaluation_criteria?.map((criterion: any, idx: number) => (
                    <Card key={idx} className="p-3 mb-2">
                      <div className="flex items-start gap-2">
                        <Star className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-theme-text mb-1">{criterion.text}</p>
                          <div className="flex gap-2 text-xs text-gray-500">
                            <span className="px-2 py-0.5 bg-blue-100 rounded text-xs">
                              {criterion.type}
                            </span>
                            {criterion.section && (
                              <span className="truncate">Sec: {criterion.section.substring(0, 50)}...</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Requirements */}
                <div className="mt-6 pt-6 border-t border-theme-border">
                  <div className="flex justify-between items-center mb-3 sticky top-0 bg-theme-paper py-2 z-10">
                    <h3 className="text-base font-semibold">Requirements ({extractedStructure.structure.requirements?.length || 0})</h3>
                    <Button onClick={() => {
                      const json = JSON.stringify(extractedStructure.structure.requirements, null, 2);
                      const blob = new Blob([json], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `requirements-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                    }} size="sm" variant="outline">
                      <Download className="h-4 w-4 mr-2" /> Export
                    </Button>
                  </div>
                  {extractedStructure.structure.requirements?.map((req: any, idx: number) => (
                    <Card key={idx} className="p-3 mb-2">
                      <div className="flex items-start gap-2">
                        <CheckCircle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                          req.type === 'mandatory' ? 'text-red-500' : 'text-blue-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-theme-text mb-1">{req.text}</p>
                          <div className="flex gap-2 text-xs text-gray-500">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              req.type === 'mandatory' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {req.type}
                            </span>
                            {req.section && (
                              <span className="truncate">Sec: {req.section.substring(0, 50)}...</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {reviewTab === 'markdown' && extractedStructure.structure && (
              <MarkdownTemplateViewer
                structure={extractedStructure.structure}
                editable={false}
              />
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Step 3: Job monitoring
  if (submittedJobId) {
    const jobDone = jobStatus?.status === 'completed';
    const jobFailed = jobStatus?.status === 'failed';
    return (
      <div className="space-y-4">
        <Card>
          <div className="space-y-4">
            <div className="py-2 text-center">
              <h3 className="mb-2 text-lg font-semibold">
                {jobDone ? 'Proposal ready' : jobFailed ? 'Proposal generation failed' : 'Proposal generation in progress'}
              </h3>
              <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                {jobDone
                  ? 'Your proposal finished generating. Use the button below to open it, or close this dialog anytime.'
                  : jobFailed
                    ? 'Something went wrong. Check the status card below for details; you can retry from the proposal wizard.'
                    : proposalGenerationInProgressBody()}
              </p>
              {!jobDone && !jobFailed && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                  <p className="font-medium">You can close this window</p>
                  <p className="mt-1">
                    The job continues in the background. Use{' '}
                    <strong>Proposal Management</strong> to watch progress, or wait here — when finished, use{' '}
                    <strong>View Proposal</strong> below.
                  </p>
                </div>
              )}
            </div>
            <ReportJobMonitor
              jobId={submittedJobId}
              longRunningHint={reportGenerationRunningFooterNote()}
              onComplete={(reportId) => {
                if (onComplete) {
                  onComplete(reportId);
                }
              }}
              onViewReport={(reportId) => {
                window.location.href = `/rfp/proposals-management?tab=list&reportId=${encodeURIComponent(reportId)}`;
              }}
            />
            {onCancel && (
              <div className="pt-2 border-t border-theme-border">
                <Button
                  variant="outline"
                  onClick={onCancel}
                  className="w-full"
                >
                  Close (Job continues in background)
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Note: Template generation loading is now shown inside the RFP upload modal
  // So we don't need a separate loading screen here

  // Show RFP processing progress when RFP upload method is selected
  if (selectedMethod === 'upload_rfp' && rfpJobStream.isStreaming) {
    const getStageDescription = (stage: string) => {
      switch (stage) {
        case 'queued':
        case 'waiting':
          return 'Waiting for processing to start...';
        case 'parsing':
          return 'Parsing RFP document structure...';
        case 'chunking':
          return 'Chunking document content...';
        case 'indexing':
          return 'Indexing document for search...';
        case 'extracting':
          return 'Extracting RFP structure and requirements...';
        case 'processing':
          return 'Processing RFP document...';
        case 'complete':
          return 'Processing complete!';
        case 'error':
          return 'Processing failed';
        default:
          return 'Processing RFP document...';
      }
    };

    return (
      <Card title="Process RFP Document">
        <div className="py-8">
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-100 via-indigo-100 to-blue-100  rounded-lg border-2 border-blue-300 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                {rfpJobStream.stage === 'parsing' ? (
                  <FileText className="h-5 w-5 text-blue-600 animate-pulse" />
                ) : rfpJobStream.stage === 'chunking' ? (
                  <Search className="h-5 w-5 text-blue-600 animate-bounce" />
                ) : rfpJobStream.stage === 'indexing' ? (
                  <Zap className="h-5 w-5 text-blue-600 animate-spin" />
                ) : rfpJobStream.stage === 'extracting' ? (
                  <Brain className="h-5 w-5 text-blue-600 animate-pulse" />
                ) : (
                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                )}
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                </span>
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-blue-900 mb-1">
                  {rfpJobStream.message || getStageDescription(rfpJobStream.stage)}
                </div>
                <div className="text-xs text-blue-700">
                  {getStageDescription(rfpJobStream.stage)}
                </div>
              </div>
              {rfpJobStream.elapsed > 0 && (
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">{rfpJobStream.elapsed.toFixed(1)}s</div>
                  <div className="text-xs text-blue-500">elapsed</div>
                </div>
              )}
              {rfpJobStream.isStreaming && (
                <button
                  onClick={() => rfpJobStream.cancelStream()}
                  className="ml-2 flex items-center gap-1 text-sm text-red-600 hover:text-red-700 "
                >
                  <StopCircle className="h-4 w-4" />
                  Cancel
                </button>
              )}
            </div>
            
            {/* Progress stages */}
            <div className="flex items-center justify-between text-xs text-blue-700 mb-2">
              <span className={rfpJobStream.stage === 'parsing' ? 'text-blue-900 font-bold' : ''}>
                Parse
              </span>
              <span className={rfpJobStream.stage === 'chunking' ? 'text-blue-900 font-bold' : ''}>
                Chunk
              </span>
              <span className={rfpJobStream.stage === 'indexing' ? 'text-blue-900 font-bold' : ''}>
                Index
              </span>
              <span className={rfpJobStream.stage === 'extracting' ? 'text-blue-900 font-bold' : ''}>
                Extract
              </span>
              <span className={rfpJobStream.stage === 'complete' ? 'text-blue-900 font-bold' : ''}>
                Done
              </span>
            </div>
            <div className="h-2 bg-blue-200 rounded-full overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300 relative"
                style={{
                  width: `${rfpJobStream.progress}%`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
              </div>
            </div>
            
            {rfpJobStream.stage === 'complete' && (
              <div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-md">
                <CheckCircle className="h-3 w-3" />
                <span className="font-medium">Processing complete! Generating template...</span>
              </div>
            )}
            
            {rfpJobStream.stage === 'error' && (
              <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 px-3 py-2 rounded-md">
                <AlertCircle className="h-3 w-3" />
                <span className="font-medium">{rfpJobStream.error || 'Processing failed'}</span>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Enhanced loading state with streaming progress (only for AI-derived)
  if (selectedMethod === 'ai_derived' && isLoading) {
    const getStageDescription = (stage: string) => {
      switch (stage) {
        case 'retrieval':
        case 'started':
          return 'Searching through past proposals, capability statements, and company documents to find relevant experience and expertise...';
        case 'retrieval_complete':
          return 'Analyzing retrieved documents to understand project requirements and identify best practices from past proposals...';
        case 'document_parsing':
          return 'Parsing uploaded RFP document structure and extracting sections...';
        case 'structure_extraction':
          return 'Extracting hierarchical section structure (M1-M4, R1-R10, etc.)...';
        case 'structure_extraction_complete':
          return 'Structure extraction complete! Review the extracted sections, requirements, and evaluation criteria.';
        case 'awaiting_approval':
          return 'Structure extracted — continuing to template and questions…';
        case 'section_mapping':
          return 'Mapping document sections to proposal template structure...';
        case 'question_generation':
          return 'Generating comprehensive questions for each section...';
        case 'keyword_extraction':
          return 'Extracting keywords, evaluation criteria, and compliance requirements...';
        case 'stats_analysis':
          return 'Analyzing document statistics and metadata for context...';
        case 'compliance_matrix_extraction':
          return 'Extracting compliance matrix and scoring structure...';
        case 'parsing':
          return 'Extracting proposal structure and generating comprehensive questions tailored to this opportunity...';
        case 'fallback':
          return 'Using policy pack template (RAG analysis unavailable)...';
        case 'complete':
          return 'Template generation complete! Proceeding to configuration...';
        default:
          return 'Preparing proposal template...';
      }
    };
    
    const loadingMessages = [
      'Searching past proposals and capability statements...',
      'Analyzing opportunity requirements and company expertise...',
      'Extracting proposal structure from best practices...',
      'Generating comprehensive questions for each section...',
    ];
    const messageIndex = Math.floor((templateStream.elapsed || 0) / 2) % loadingMessages.length;
    
    return (
      <Card title="Generate Proposal Template">
        <div className="py-8">
          {/* Clear description of what's happening */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              What's happening?
            </h3>
            <p className="text-xs text-blue-800 leading-relaxed">
              We're analyzing this opportunity and your company's past proposals to create a customized proposal template. 
              This includes searching through past proposals, capability statements, and company documents to identify 
              relevant experience, then generating comprehensive questions for each section that will help create a 
              thorough, winning proposal.
            </p>
          </div>

          {/* Enhanced Streaming status indicator */}
          {templateStream.isStreaming && (
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-100 via-indigo-100 to-blue-100  rounded-lg border-2 border-blue-300 shadow-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  {templateStream.stage === 'retrieval' || templateStream.stage === 'started' || templateStream.stage === 'retrieval_complete' ? (
                    <Search className="h-5 w-5 text-blue-600 animate-bounce" />
                  ) : templateStream.stage === 'document_parsing' || templateStream.stage === 'structure_extraction' || templateStream.stage === 'section_mapping' ? (
                    <FileText className="h-5 w-5 text-blue-600 animate-pulse" />
                  ) : templateStream.stage === 'question_generation' || templateStream.stage === 'keyword_extraction' || templateStream.stage === 'stats_analysis' || templateStream.stage === 'compliance_matrix_extraction' ? (
                    <Brain className="h-5 w-5 text-blue-600 animate-pulse" />
                  ) : templateStream.stage === 'parsing' || templateStream.stage === 'fallback' ? (
                    <Brain className="h-5 w-5 text-blue-600 animate-pulse" />
                  ) : (
                    <Zap className="h-5 w-5 text-blue-600 animate-spin" />
                  )}
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                  </span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-blue-900 mb-1">
                    {templateStream.message || getStageDescription(templateStream.stage || '')}
                  </div>
                  <div className="text-xs text-blue-700 mt-1">
                    {getStageDescription(templateStream.stage || '')}
                  </div>
                </div>
                {templateStream.elapsed > 0 && (
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{templateStream.elapsed.toFixed(1)}s</div>
                    <div className="text-xs text-blue-500">elapsed</div>
                  </div>
                )}
                {templateStream.isStreaming && (
                  <button
                    onClick={() => templateStream.cancelStream()}
                    className="ml-2 flex items-center gap-1 text-sm text-red-600 hover:text-red-700 "
                  >
                    <StopCircle className="h-4 w-4" />
                    Cancel
                  </button>
                )}
              </div>
              
              {/* Enhanced Progress bar with stages */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-blue-700 font-medium">
                  <span className={templateStream.stage === 'retrieval' || templateStream.stage === 'started' || templateStream.stage === 'retrieval_complete' ? 'text-blue-900 font-bold' : ''}>
                    Search
                  </span>
                  <span className={templateStream.stage === 'document_parsing' || templateStream.stage === 'structure_extraction' || templateStream.stage === 'section_mapping' ? 'text-blue-900 font-bold' : ''}>
                    Extract
                  </span>
                  <span className={templateStream.stage === 'question_generation' || templateStream.stage === 'keyword_extraction' || templateStream.stage === 'stats_analysis' || templateStream.stage === 'compliance_matrix_extraction' || templateStream.stage === 'parsing' || templateStream.stage === 'fallback' ? 'text-blue-900 font-bold' : ''}>
                    Generate
                  </span>
                  <span className={templateStream.stage === 'complete' || templateStream.stage === 'structure_extraction_complete' ? 'text-blue-900 font-bold' : ''}>
                    Done
                  </span>
                </div>
                <div className="h-2 bg-blue-200 rounded-full overflow-hidden shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 transition-all duration-500 ease-out relative overflow-hidden"
                    style={{
                      width: templateStream.stage === 'retrieval' || templateStream.stage === 'started' ? '25%' :
                             templateStream.stage === 'retrieval_complete' ? '35%' :
                             templateStream.stage === 'document_parsing' ? '40%' :
                             templateStream.stage === 'structure_extraction' ? '50%' :
                             templateStream.stage === 'structure_extraction_complete' ? '90%' :
                             templateStream.stage === 'awaiting_approval' ? '95%' :
                             templateStream.stage === 'section_mapping' ? '60%' :
                             templateStream.stage === 'question_generation' ? '75%' :
                             templateStream.stage === 'keyword_extraction' ? '80%' :
                             templateStream.stage === 'stats_analysis' ? '85%' :
                             templateStream.stage === 'compliance_matrix_extraction' ? '90%' :
                             templateStream.stage === 'parsing' || templateStream.stage === 'fallback' ? '70%' :
                             templateStream.stage === 'complete' ? '100%' : 
                             templateStream.progress ? `${templateStream.progress}%` : '10%',
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                  </div>
                </div>
              </div>
              
              {templateStream.evidenceCount > 0 && (
                <div className="mt-3 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 px-3 py-2 rounded-md">
                  <FileText className="h-3 w-3" />
                  <span className="font-medium">{templateStream.evidenceCount} sources found</span>
                  {templateStream.retrievalTime > 0 && (
                    <span className="text-blue-500">• {templateStream.retrievalTime.toFixed(1)}s</span>
                  )}
                </div>
              )}

              {/* NOTE: Approval UI removed - auto-transitions to structure review screen */}
            </div>
          )}
          
          {!templateStream.isStreaming && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
              <p className="text-gray-600">Loading proposal template...</p>
            </div>
          )}
        </div>
      </Card>
    );
  }

  if (hasError) {
    const errorMessage = templateStream.error || templateError?.message || 'Could not load proposal template';
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-theme-text font-semibold mb-2">
            Failed to Load Template
          </p>
          <p className="text-gray-600 text-sm mb-4">
            {errorMessage}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => {
              if (useStreaming) {
                templateStream.reset();
                templateStream.startStream(opportunityId, projectId!);
              } else {
                window.location.reload();
              }
            }}>
              Retry
            </Button>
            <Button variant="outline" onClick={() => setUseStreaming(!useStreaming)}>
              {useStreaming ? 'Use Non-Streaming' : 'Use Streaming'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Show loading state if streaming is active (don't show "No Template Available" error)
  if (!hasValidTemplate && !templateStream.isStreaming && !isGeneratingTemplate) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
          <p className="text-theme-text font-semibold mb-2">
            No Template Available
          </p>
          <p className="text-gray-600 text-sm mb-4">
            Could not generate a proposal template for this opportunity.
          </p>
          <Button onClick={() => {
            if (useStreaming) {
              templateStream.reset();
              templateStream.startStream(opportunityId, projectId!);
            } else {
              window.location.reload();
            }
          }}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const proposalCardTitle =
    step === 1 && templateSource === 'production_template'
      ? 'Production Template Generation'
      : 'Create Proposal';

  return (
    <div className="mx-auto w-full min-w-0 max-w-full space-y-6">
      <Card title={proposalCardTitle} subtitle={`Step ${step} of 2`}>
        {/* Method indicator */}
        {selectedMethod && (
          <div className="mb-4 space-y-2">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-blue-900">
                <span className="font-medium">Template Method:</span>
                <span>
                  {selectedMethod === 'upload_rfp' && '📄 RFP Document Upload'}
                  {selectedMethod === 'import_report' && '📋 Previous Report Import'}
                  {selectedMethod === 'ai_derived' && '🤖 AI-Derived Template'}
                  {selectedMethod === 'production_template' && 'AI-Derived Template'}
                </span>
                {templateSource && (
                  <span className="text-xs text-blue-700">
                    ({templateSource})
                  </span>
                )}
              </div>
            </div>
            
            {/* Artifact info display for RFP uploads */}
            {selectedMethod === 'upload_rfp' && uploadedArtifacts.length > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-xs font-medium text-green-900 mb-2">
                  Uploaded Documents:
                </div>
                <div className="space-y-1.5">
                  {uploadedArtifacts.map((artifact) => (
                    <div key={artifact.id} className="flex items-center justify-between text-xs text-green-800">
                      <span className="truncate flex-1 mr-2" title={artifact.filename}>
                        📄 {artifact.filename}
                      </span>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-green-700">
                          {artifact.chunks_count} chunks
                        </span>
                        <span className="text-green-700">
                          {artifact.entities_count} entities
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 1 && templateSource === 'production_template' && productionTemplateResultRef.current && (
          <TemplatePreview
            result={productionTemplateResultRef.current}
            opportunityId={opportunityId}
            opportunityTitle={opportunityTitle}
            onClose={() => {
              setStep(0);
              setSelectedMethod(null);
              setQuestions([]);
              setTemplateSource('');
              productionTemplateResultRef.current = null;
            }}
            onRegenerate={() => {
              setStep(0);
              setSelectedMethod('production_template');
              setShowProductionTemplateModal(true);
            }}
            onProposalSubmitted={(jobId) => {
              setSubmittedJobId(jobId);
              setStep(3);
              onComplete?.(jobId);
            }}
          />
        )}

        {step === 1 && templateSource !== 'production_template' && (
          <div className="space-y-4">
            {/* Compressed opportunity details */}
            <div className="flex items-center justify-between gap-4 pb-2 border-b border-theme-border">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-theme-text truncate">{opportunityTitle}</h3>
                <p className="text-xs text-theme-text-muted mt-0.5">{clientName || 'Government Agency'}</p>
              </div>
              <div className="w-48 flex-shrink-0">
                <Input
                  value={proposalName}
                  onChange={(e) => setProposalName(e.target.value)}
                  placeholder="Proposal name (optional)"
                />
              </div>
            </div>

            {/* Compressed template source info */}
            {finalTemplate && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2 text-xs text-blue-900">
                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">
                  Template Source: {(finalTemplate as any).template_source === 'rag_analysis_with_policy_pack' 
                    ? 'RAG + Policy Pack' 
                    : (finalTemplate as any).template_source === 'rfp_documents'
                    ? 'RFP Documents'
                    : (finalTemplate as any).template_source === 'previous_report_import'
                    ? 'Previous Report'
                    : (finalTemplate as any).template_source || 'Policy Pack'}
                  {templateStream.totalTime > 0 && (
                    <span className="ml-2 text-blue-700">• {templateStream.totalTime.toFixed(1)}s</span>
                  )}
                </span>
              </div>
            )}

            {/* Template Variables Editor (Above tabs) */}
            {(finalTemplate as any)?.variables && (
              <div className="mb-4">
                <TemplateVariablesEditor
                  variables={(finalTemplate as any).variables}
                  readOnly={false}
                />
              </div>
            )}

            {/* V3 RFP Structure Extraction Status (NEW) */}
            {selectedMethod === 'ai_derived' && (
              <div className="mb-4">
                {v3ExtractionState.status === 'pending' && !v3ExtractionState.isExtracting && (
                  <Card className="p-4 bg-blue-50 border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-blue-600" />
                        <div>
                          <h4 className="font-medium text-blue-900">V3 RFP Structure Extraction</h4>
                          <p className="text-sm text-blue-700">
                            Extract document structure for improved proposal generation
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => v3ExtractionActions.triggerExtraction(rfpArtifactIds.length > 0 ? rfpArtifactIds : undefined)}
                        disabled={!projectId}
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Extract Structure
                      </Button>
                    </div>
                  </Card>
                )}

                {v3ExtractionState.isExtracting && (
                  <Card className="p-4 bg-yellow-50 border-yellow-200">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-5 w-5 text-yellow-600 animate-spin" />
                      <div>
                        <h4 className="font-medium text-yellow-900">Extracting RFP Structure...</h4>
                        <p className="text-sm text-yellow-700">
                          Analyzing document structure, sections, and evaluation criteria
                        </p>
                      </div>
                    </div>
                  </Card>
                )}

                {v3ExtractionState.status === 'completed' && v3ExtractionState.structure && (
                  <Card className="p-4 bg-green-50 border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <div>
                          <h4 className="font-medium text-green-900">V3 Structure Extracted</h4>
                          <p className="text-sm text-green-700">
                            {v3ExtractionState.structure.sections.length} sections, {v3ExtractionState.structure.requirements?.length || 0} requirements, {v3ExtractionState.structure.evaluation_criteria?.length || 0} criteria
                            {v3ExtractionState.qualityScore && <span className="ml-2">• Quality: {v3ExtractionState.qualityScore}/100</span>}
                          </p>
                        </div>
                      </div>
                      {v3ExtractionState.canReExtract && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => v3ExtractionActions.triggerExtraction(rfpArtifactIds.length > 0 ? rfpArtifactIds : undefined, true)}
                        >
                          Re-Extract
                        </Button>
                      )}
                    </div>
                  </Card>
                )}

                {v3ExtractionState.status === 'failed' && (
                  <Card className="p-4 bg-red-50 border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        <div>
                          <h4 className="font-medium text-red-900">Extraction Failed</h4>
                          <p className="text-sm text-red-700">
                            {v3ExtractionState.errorMessage || 'Failed to extract RFP structure'}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => v3ExtractionActions.triggerExtraction(rfpArtifactIds.length > 0 ? rfpArtifactIds : undefined, true)}
                      >
                        Retry
                      </Button>
                    </div>
                  </Card>
                )}
              </div>
            )}

            {/* Build tabs array for Tabs component */}
            <Tabs
              tabs={buildTemplateTabs(finalTemplate, questions, v3ExtractionState.structure)}
              defaultTab={v3ExtractionState.structure && v3ExtractionState.structure.sections.length > 0 ? "v3-structure" : "sections"}
            />

            {/* Entity types summary (Move above tabs) */}
            {(finalTemplate as any)?.entity_types_by_question && Object.keys((finalTemplate as any).entity_types_by_question).length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-xs font-medium text-blue-900 mb-2">
                  Entity Types Being Queried:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(new Set(
                    Object.values((finalTemplate as any).entity_types_by_question).flat() as string[]
                  )).map((entityType: string) => (
                    <span
                      key={entityType}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                      title={`${entityType} entities will be queried for relevant questions`}
                    >
                      {entityType.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
                <div className="mt-2 text-xs text-blue-700">
                  Only internal entities are queried (external opportunities are excluded)
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => {
                setStep(0);
                setSelectedMethod(null);
                setQuestions([]);
                setTemplateSource('');
              }}>
                Change Method
              </Button>
              <Button onClick={() => setStep(2)} disabled={questions.length === 0}>
                Next: Review Questions
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Questions</h3>
              <Button size="sm" onClick={handleAddQuestion}>
                <Plus className="h-4 w-4" />
                Add Question
              </Button>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
              {questions.map((q, idx) => (
                <div key={idx} className="border border-theme-border rounded-lg p-3 bg-theme-elevated">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs font-medium text-theme-text-secondary mb-1.5">
                        Section
                      </label>
                      <Select
                        value={q.section}
                        onChange={(e) => handleQuestionChange(idx, 'section', e.target.value)}
                        options={(finalTemplate?.sections || []).map((s: any) => ({ value: s.name, label: s.name }))}
                        title={q.section}
                        className="text-sm text-theme-text"
                      />
                      {/* Full section name displayed below select for reference */}
                      <div className="mt-1.5 text-xs text-theme-text-muted line-clamp-2" title={q.section}>
                        {q.section}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveQuestion(idx)}
                      className="flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-theme-text-secondary mb-1.5">
                      Question
                    </label>
                    <textarea
                      placeholder="Question"
                      value={q.question}
                      onChange={(e) => handleQuestionChange(idx, 'question', e.target.value)}
                      rows={Math.min(Math.max(Math.ceil(q.question.length / 80), 2), 8)}
                      className="block w-full rounded-lg border border-theme-border px-3 py-2 text-sm bg-theme-paper text-theme-text placeholder-theme-text-disabled focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[60px] max-h-[300px] overflow-y-auto"
                      style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                    />
                    {/* Entity types that will be queried for this question */}
                    {(finalTemplate as any)?.entity_types_by_question && (finalTemplate as any).entity_types_by_question[q.question] && (finalTemplate as any).entity_types_by_question[q.question].length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="text-xs text-theme-text-muted font-medium">Querying:</span>
                        {(finalTemplate as any).entity_types_by_question[q.question].map((entityType: string, etIdx: number) => (
                          <span
                            key={etIdx}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                            title={`This question will query ${entityType} entities`}
                          >
                            {entityType.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-900">
                <strong>💡 Tip:</strong> After the proposal is generated, you can add custom sections 
                (AI-generated or manual) via the "Custom Sections" tab in the Report Viewer.
              </p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={questions.length === 0 || submitMutation.isPending}
                isLoading={submitMutation.isPending}
              >
                <FileText className="h-4 w-4" />
                Generate Proposal
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Review Structure Modal */}
      {showReviewModal && extractedStructure && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50" onClick={() => setShowReviewModal(false)}>
          <div className="flex min-h-full items-center justify-center p-4">
            <div 
              className="relative bg-theme-paper rounded-lg shadow-xl w-full max-w-6xl border border-theme-border"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-theme-border">
                <div>
                  <h2 className="text-2xl font-bold text-theme-text">
                    Review Extracted Structure
                  </h2>
                  <p className="text-sm text-theme-text-secondary mt-1">
                    Quality: {extractedStructure.quality_score || 90}/100 • {extractedStructure.structure?.sections?.length || 0} sections
                  </p>
                </div>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-400 hover:text-gray-600 "
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Body with Tabs */}
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="mb-4 border-b border-theme-border">
                  <nav className="flex space-x-4">
                    <button
                      onClick={() => setReviewTab('outline')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        reviewTab === 'outline'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Structure Outline
                    </button>
                    <button
                      onClick={() => setReviewTab('criteria')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        reviewTab === 'criteria'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Criteria & Requirements
                    </button>
                    <button
                      onClick={() => setReviewTab('markdown')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        reviewTab === 'markdown'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Markdown Export
                    </button>
                  </nav>
                </div>

                {reviewTab === 'outline' && extractedStructure.structure && (
                  <EvaluatorLedOutlineView
                    structure={extractedStructure.structure}
                    qualityScore={extractedStructure.quality_score}
                  />
                )}

                {reviewTab === 'criteria' && extractedStructure.structure && (
                  <NormalizedCriteriaIndex
                    evaluationCriteria={extractedStructure.structure.evaluation_criteria || []}
                    requirements={extractedStructure.structure.requirements || []}
                    qualityScore={extractedStructure.quality_score}
                  />
                )}

                {reviewTab === 'markdown' && extractedStructure.structure && (
                  <MarkdownTemplateViewer
                    structure={extractedStructure.structure}
                    editable={false}
                  />
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-theme-border bg-theme-elevated">
                <Button
                  onClick={() => setShowReviewModal(false)}
                  variant="outline"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setShowReviewModal(false);
                    // User can then click Approve button in the main UI
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Looks Good
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Build tabs array for template view
 */
function buildTemplateTabs(finalTemplate: any, questions: any[], v3Structure?: any): Tab[] {
  const tabs: Tab[] = [];

  // V3 RFP Structure Tab (NEW - Priority)
  if (v3Structure && v3Structure.sections && v3Structure.sections.length > 0) {
    tabs.push({
      id: 'v3-structure',
      label: 'RFP Structure (V3)',
      badge: v3Structure.sections.length,
      content: (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium">Extracted RFP Structure</h3>
            <div className="flex items-center gap-2">
              {v3Structure.quality_metrics?.quality_score && (
                <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">
                  Quality: {v3Structure.quality_metrics.quality_score}/100
                </span>
              )}
              <span className="text-sm text-theme-text-secondary">
                {v3Structure.sections.length} sections
              </span>
            </div>
          </div>
          <EvaluatorLedOutlineView
            structure={v3Structure}
          />
        </div>
      )
    });
  }

  // V3 Evaluation Criteria Tab (NEW - Priority)
  if (v3Structure && v3Structure.evaluation_criteria && v3Structure.evaluation_criteria.length > 0) {
    tabs.push({
      id: 'v3-criteria',
      label: 'Evaluation Criteria (V3)',
      badge: v3Structure.evaluation_criteria.length,
      content: (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium">Extracted Evaluation Criteria</h3>
            <span className="text-sm text-theme-text-secondary">
              {v3Structure.evaluation_criteria.length} criteria
            </span>
          </div>
          <NormalizedCriteriaIndex
            evaluationCriteria={v3Structure.evaluation_criteria}
            requirements={v3Structure.requirements}
          />
        </div>
      )
    });
  }

  // Always include Sections & Questions tab
  tabs.push({
    id: 'sections',
    label: 'Sections & Questions',
    badge: finalTemplate?.sections?.length || 0,
    content: (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium">Proposal Sections</h3>
          <span className="text-sm text-theme-text-secondary">
            {finalTemplate?.sections?.length || 0} sections, {finalTemplate?.default_questions?.length || questions.length} questions
          </span>
        </div>
        <div className="bg-theme-elevated border border-theme-border rounded-lg p-4 max-h-[500px] overflow-y-auto">
          {finalTemplate?.sections && finalTemplate.sections.length > 0 ? (
            <div className="space-y-3">
              {finalTemplate.sections.map((section: any, idx: number) => {
                const sectionName = section.name || section.title || `Section ${idx + 1}`;
                const allQuestions = finalTemplate?.default_questions || questions || [];
                
                // Find questions that match this section
                const sectionQuestions = allQuestions.filter((q: any) => {
                  const qSection = q.section || '';
                  if (!qSection) return false;
                  
                  // Exact match
                  if (qSection === sectionName || qSection === section.title || qSection === section.name) {
                    return true;
                  }
                  
                  // Normalized match (case-insensitive, trim numbers)
                  const normalize = (str: string) => str.replace(/^\d+\.\s*/, '').trim().toLowerCase();
                  if (normalize(qSection) === normalize(sectionName)) {
                    return true;
                  }
                  
                  return false;
                });
                
                return (
                  <Card key={idx} className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-medium text-theme-text flex-1">{sectionName}</h4>
                      {sectionQuestions.length > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 flex-shrink-0">
                          {sectionQuestions.length} {sectionQuestions.length === 1 ? 'question' : 'questions'}
                        </span>
                      )}
                    </div>
                    
                    {section.description && (
                      <p className="text-sm text-theme-text-secondary mb-2">{section.description}</p>
                    )}
                    
                    {/* CRITICAL: Display questions for this section */}
                    {sectionQuestions.length > 0 && (
                      <div className="mt-3 space-y-2 pl-3 border-l-2 border-blue-300">
                        {sectionQuestions.map((question: any, qIdx: number) => (
                          <div key={qIdx} className="text-sm text-theme-text">
                            <span className="font-medium text-blue-600">Q{qIdx + 1}:</span>{' '}
                            {question.question || question.text || 'No question text'}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {sectionQuestions.length === 0 && (
                      <div className="mt-2 text-xs text-theme-text-muted italic">
                        No questions for this section
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-theme-text-muted italic text-center py-8">
              No sections available
            </div>
          )}
        </div>
      </div>
    )
  });

  // Dedicated Questions tab (NEW - show all questions grouped by their actual section field)
  // NOTE: Question sections might not match the sections array names!
  // For example, questions might be grouped as "Technical Requirements", "Mandatory Requirements"
  // while sections array has "2. Offer Requirements.", "4. Offer Submission.", etc.
  if ((finalTemplate?.default_questions && finalTemplate.default_questions.length > 0) || (questions && questions.length > 0)) {
    const allQuestions = finalTemplate?.default_questions || questions || [];
    
    // Group questions by their actual section field (as returned by backend)
    const questionsBySection: Record<string, any[]> = {};
    allQuestions.forEach((q: any) => {
      const section = q.section || 'Uncategorized';
      if (!questionsBySection[section]) {
        questionsBySection[section] = [];
      }
      questionsBySection[section].push(q);
    });
    
    tabs.push({
      id: 'questions',
      label: 'All Questions',
      badge: allQuestions.length,
      content: (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-medium">Generated Questions</h3>
              <p className="text-xs text-theme-text-muted mt-1">
                Questions grouped by requirement category as defined in RFP
              </p>
            </div>
            <span className="text-sm text-theme-text-secondary">
              {allQuestions.length} total • {Object.keys(questionsBySection).length} categories
            </span>
          </div>
          <div className="bg-theme-elevated border border-theme-border rounded-lg p-4 max-h-[600px] overflow-y-auto">
            <div className="space-y-4">
              {Object.entries(questionsBySection)
                .sort(([a], [b]) => {
                  // Sort: Technical/Desirable/Mandatory/Eval first, then S1-S21 numerically
                  const order = ['Technical Requirements', 'Desirable Requirements', 'Mandatory Requirements', 'Evaluation Criteria Response'];
                  const aIdx = order.indexOf(a);
                  const bIdx = order.indexOf(b);
                  if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                  if (aIdx !== -1) return -1;
                  if (bIdx !== -1) return 1;
                  // Sort S1-S21 numerically
                  const aNum = a.match(/S(\d+)/)?.[1];
                  const bNum = b.match(/S(\d+)/)?.[1];
                  if (aNum && bNum) return parseInt(aNum) - parseInt(bNum);
                  return a.localeCompare(b);
                })
                .map(([sectionName, sectionQuestions], idx) => (
                  <div key={idx} className="border-b border-theme-border pb-4 last:border-0">
                    <div className="flex items-start justify-between mb-2 gap-3">
                      <h4 className="font-semibold text-theme-text flex-1">{sectionName}</h4>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 flex-shrink-0">
                        {sectionQuestions.length} {sectionQuestions.length === 1 ? 'Q' : 'Qs'}
                      </span>
                    </div>
                    <div className="space-y-3 pl-3 border-l-2 border-green-300">
                      {sectionQuestions.map((question: any, qIdx: number) => (
                        <div key={qIdx} className="text-sm bg-theme-paper rounded p-2 border border-theme-border">
                          <div className="font-medium text-green-600 mb-1 text-xs">
                            Q{qIdx + 1}
                          </div>
                          <div className="text-theme-text leading-relaxed" dangerouslySetInnerHTML={{ __html: formatQuestionTextForDisplay(question.question || question.text) || 'No question text' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )
    });
  }

  // Compliance Matrix tab
  if (finalTemplate?.compliance_matrix) {
    tabs.push({
      id: 'compliance',
      label: 'Compliance Matrix',
      content: (
        <ComplianceMatrixView
          complianceMatrix={finalTemplate.compliance_matrix}
          readOnly={false}
        />
      )
    });
  }

  // Criteria Index tab (OLD - now secondary to V3)
  if (finalTemplate?.gate_criteria || finalTemplate?.scored_criteria) {
    tabs.push({
      id: 'criteria',
      label: 'Criteria Index (Legacy)',
      content: (
        <NormalizedCriteriaIndex
          criteriaIndex={finalTemplate?.normalized_criteria_index}
          gateCriteria={finalTemplate?.gate_criteria}
          scoredCriteria={finalTemplate?.scored_criteria}
        />
      )
    });
  }

  // QA Pass tab
  if (finalTemplate?.qa_pass_questions) {
    tabs.push({
      id: 'qa',
      label: 'QA Pass',
      badge: finalTemplate.qa_pass_questions.length,
      content: (
        <QAPassQuestions
          questions={finalTemplate.qa_pass_questions}
          readOnly={false}
        />
      )
    });
  }

  // Evaluator Outline tab
  if (finalTemplate?.evaluator_led_outline) {
    tabs.push({
      id: 'outline',
      label: 'Evaluator Outline',
      content: (
        <EvaluatorLedOutlineView
          outline={finalTemplate.evaluator_led_outline}
        />
      )
    });
  }

  // Markdown View tab
  if (finalTemplate?.markdown_output) {
    tabs.push({
      id: 'markdown',
      label: 'Markdown View',
      content: (
        <MarkdownTemplateViewer
          initialMarkdown={finalTemplate.markdown_output}
          editable={false}
        />
      )
    });
  }

  return tabs;
}

// Default export for compatibility
export default ProposalBuilder;

